import React from 'react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Shield, Users, Clock, Award, ArrowRight, MapPin } from 'lucide-react';
import { whyChooseUs, businessInfo } from '../mock';

const AboutSection = () => {
  const iconMap = {
    Shield: Shield,
    Users: Users,
    Clock: Clock,
    Award: Award,
    MapPin: MapPin
  };

  const handleGetQuote = () => {
    window.location.href = `mailto:${businessInfo.email}?subject=Quote Request&body=Hi, I would like to get a quote for shutter installation. Please contact me with more details.`;
  };

  return (
    <section id="about" className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          
          {/* Left Content */}
          <div className="space-y-8">
            <div className="space-y-6">
              <div className="inline-flex items-center px-4 py-2 bg-white rounded-full text-sm font-medium text-gray-700 shadow-sm">
                About Homeshutter
              </div>
              
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 leading-tight">
                Your Local
                <span className="block text-gray-700">South Australia</span>
                <span className="block text-gray-600">Shutter Team</span>
              </h2>
              
              <div className="space-y-4 text-gray-600 leading-relaxed">
                <p>
                  At HomeShutters, we're a local South Australian team dedicated to helping families and businesses 
                  stay secure and comfortable all year round. We believe in doing things right the first time—quality 
                  products, honest pricing, and no-pressure advice.
                </p>
                
                <p>
                  Whether it's a brand-new fit-out or a quick repair, we'll show up on time, treat your property 
                  with care, and leave you with results that last. Residential and commercial solutions available 
                  with fast repairs and maintenance when you need it.
                </p>
                
                <p>
                  We're here to help you protect what matters most — with quality, convenience, and care.
                </p>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                size="lg"
                onClick={handleGetQuote}
                className="bg-gray-900 hover:bg-gray-800 px-8 py-4 text-lg font-semibold group"
              >
                Work With Us
                <ArrowRight className="h-5 w-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
              
              <Button
                variant="outline"
                size="lg"
                onClick={() => window.location.href = `tel:${businessInfo.phone}`}
                className="border-2 border-gray-900 text-gray-900 hover:bg-gray-900 hover:text-white px-8 py-4 text-lg font-semibold"
              >
                Call {businessInfo.phone}
              </Button>
            </div>
          </div>

          {/* Right Content - Logo Display */}
          <div className="flex justify-center items-center">
            <img 
              src={businessInfo.logo}
              alt={businessInfo.name} 
              className="max-w-full h-auto max-h-80"
            />
          </div>
        </div>

        {/* Why Choose Us Section */}
        <div className="mt-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            
            {/* Why Choose Us Content */}
            <div className="space-y-6">
              <h3 className="text-2xl font-bold text-gray-900 mb-8">
                Why Choose Homeshutter?
              </h3>
              
              <div className="grid grid-cols-1 gap-6">
                {whyChooseUs.map((item, index) => {
                  const IconComponent = iconMap[item.icon];
                  
                  return (
                    <Card key={index} className="group hover:shadow-lg transition-all duration-300 border-0 shadow-md">
                      <CardContent className="p-6">
                        <div className="flex items-start space-x-4">
                          <div className="w-12 h-12 bg-gray-900 rounded-xl flex items-center justify-center group-hover:bg-gray-700 transition-colors duration-300">
                            <IconComponent className="h-6 w-6 text-white" />
                          </div>
                          <div className="flex-1">
                            <h4 className="text-lg font-semibold text-gray-900 mb-2">
                              {item.title}
                            </h4>
                            <p className="text-gray-600 leading-relaxed">
                              {item.description}
                            </p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>

            {/* Stats */}
            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <div className="grid grid-cols-3 gap-6 text-center">
                <div>
                  <div className="text-3xl font-bold text-gray-900 mb-2">500+</div>
                  <div className="text-sm text-gray-600">Projects Completed</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-gray-900 mb-2">10+</div>
                  <div className="text-sm text-gray-600">Years Experience</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-gray-900 mb-2">100%</div>
                  <div className="text-sm text-gray-600">Satisfaction Rate</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;